import { useEffect, useMemo, useState } from 'react'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ALL_FUNCTIONS_GROUP, ColumnAdapter, TableAdapter } from '@makyoapp/makyo-common'

import { useDbClientContext } from '@/context/__project__/DbClientContext'

const TEMPLATE_COMPATIBLE_COLUMNS = [
  ColumnTypes.Text,
  ColumnTypes.RichText,
  ColumnTypes.Template,
  ColumnTypes.Number,
  ColumnTypes.DateTime,
  ColumnTypes.Email,
  ColumnTypes.Phone,
  ColumnTypes.URL,
  ColumnTypes.Color,
  ColumnTypes.RowID,
  ColumnTypes.SessionID,
]

function isTextColumn(types: ColumnTypes[]) {
  return [ColumnTypes.Text, ColumnTypes.RichText, ColumnTypes.Template, ColumnTypes.RowID, ColumnTypes.SessionID].some(
    (t) => types.includes(t)
  )
}

export function useDbDataV2(
  columnTypes: ColumnTypes[] = Object.values(ColumnTypes),
  excludeColIds: string[] = [],
  dbId?: string,
  tableId?: string,
  colId?: string,
  isSingleCell?: boolean
) {
  const [tbl, setTbl] = useState<string | undefined>(dbId && tableId ? tableId : undefined)
  const [col, setCol] = useState<string | undefined>(dbId && tableId && colId ? colId : undefined)

  const { tables } = useDbClientContext()

  let includedColumnTypes = columnTypes.length > 0 ? columnTypes : Object.values(ColumnTypes)

  let isFunctionColumnType = includedColumnTypes.includes(ColumnTypes.Function)

  const tblOptions = useMemo(() => {
    return [{ id: '', name: 'Select Table' } as TableAdapter, ...tables]
  }, [tables])

  const colOptions = useMemo(() => {
    const table = tables.find((tb) => tb.id === tbl)

    let filtered =
      table?.columns.filter((c) => {
        if (!isSingleCell && !isFunctionColumnType) {
          if (c.type === ColumnTypes.Function) {
            const item = ALL_FUNCTIONS_GROUP.find((f) => f.id === c.settings?.functionOptions?.functionValue?.item?.id)
            return item && item.returnType ? includedColumnTypes.includes(item.returnType) : true // for the function with unknown return type, we might still want to show it
          }

          // If columnTypes includes Template, filter for TEMPLATE_COMPATIBLE_COLUMNS
          if (includedColumnTypes.includes(ColumnTypes.Template) && includedColumnTypes.length === 1) {
            return TEMPLATE_COMPATIBLE_COLUMNS.includes(c.type)
          }

          if (table?.id !== window.appUserTableID) {
            return includedColumnTypes.includes(c.type)
          }
        }

        return true
      }) ?? []

    if (excludeColIds.length > 0) {
      filtered = filtered.filter((c) => !excludeColIds.includes(c.id))
    }

    // if not filter result, default to show all columns instead (except excluded columns)
    if (filtered.length === 0) {
      filtered = table?.columns.filter((c) => !excludeColIds.includes(c.id)) ?? []
    }

    if (isTextColumn(includedColumnTypes) && filtered.length > 0) {
      filtered = [{ id: '_id', name: 'Row ID', type: ColumnTypes.RowID } as ColumnAdapter, ...filtered]
    }

    return [{ id: '', name: 'Select Column' } as ColumnAdapter, ...filtered]
  }, [tables, excludeColIds, includedColumnTypes, tbl, isSingleCell, isFunctionColumnType])

  useEffect(() => {
    const hasCol = colOptions.find((c) => {
      return c.id === col
    })
    if (!hasCol) {
      setCol('')
    }
  }, [col, setCol, colOptions])

  useEffect(() => {
    if (!!colOptions.length && !col) {
      let foundFirstCol = colOptions.find((c) => !!c.id)?.id
      if (foundFirstCol) {
        setCol(foundFirstCol)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    tbl,
    setTbl,
    col,
    setCol,
    tblOptions,
    colOptions,
  }
}
